import { Cell, CELL_BORDER_CLICK_BUFFER, State } from "../types";

export type SetMouseDragPositionAction = Readonly<{
  position: [number, number];
  shiftClick: boolean;
  scale: number;
  type: "SET MOUSE DRAG POSITION";
}>;

const leftBorderSelected = (
  cell: Cell,
  x: number,
  y: number,
  wholeBorder: boolean,
  selectedPosition: number | null,
  scale: number
): boolean => {
  return cell.minX > 0 &&
  Math.abs(cell.minX - x) < (CELL_BORDER_CLICK_BUFFER / scale) &&
  (wholeBorder || (y >= cell.minY && y <= cell.maxY)) &&
  (selectedPosition === null || cell.minX === selectedPosition);
}
const rightBorderSelected = (
  cell: Cell,
  x: number,
  y: number,
  zoneWidth: number,
  wholeBorder: boolean,
  selectedPosition: number | null,
  scale: number
): boolean =>
  cell.maxX < zoneWidth &&
  Math.abs(cell.maxX - x) < (CELL_BORDER_CLICK_BUFFER / scale) &&
  (wholeBorder || (y >= cell.minY && y <= cell.maxY)) &&
  (selectedPosition === null || cell.maxX === selectedPosition);
const topBorderSelected = (
  cell: Cell,
  x: number,
  y: number,
  wholeBorder: boolean,
  selectedPosition: number | null,
  scale: number
): boolean =>
  cell.minY > 0 &&
  Math.abs(cell.minY - y) < (CELL_BORDER_CLICK_BUFFER / scale) &&
  (wholeBorder || (x >= cell.minX && x <= cell.maxX)) &&
  (selectedPosition === null || cell.minY === selectedPosition);
const bottomBorderSelected = (
  cell: Cell,
  x: number,
  y: number,
  zoneHeight: number,
  wholeBorder: boolean,
  selectedPosition: number | null,
  scale: number
): boolean =>
  cell.maxY < zoneHeight &&
  Math.abs(cell.maxY - y) < (CELL_BORDER_CLICK_BUFFER / scale) &&
  (wholeBorder || (x >= cell.minX && x <= cell.maxX)) &&
  (selectedPosition === null || cell.maxY === selectedPosition);

const getSelectedBorder = (
  cell: Cell,
  x: number,
  y: number,
  zoneWidth: number,
  zoneHeight: number,
  shiftClick: boolean,
  selectedPosition: number | null,
  scale: number
): Cell["selectedBorder"] => {
  if (leftBorderSelected(cell, x, y, !shiftClick, selectedPosition, scale)) {
    return "left";
  }
  if (rightBorderSelected(cell, x, y, zoneWidth, !shiftClick, selectedPosition, scale)) {
    return "right";
  }
  if (topBorderSelected(cell, x, y, !shiftClick, selectedPosition, scale)) {
    return "top";
  }
  if (bottomBorderSelected(cell, x, y, zoneHeight, !shiftClick, selectedPosition, scale)) {
    return "bottom";
  }
  
  return null;
};
// tslint:disable-next-line: cyclomatic-complexity
export const setMouseDragPosition = (state: State, action: SetMouseDragPositionAction): State => {
  if (state.mouseDragStart !== null) {
    return state;
  }
  let selectedPosition: number | null = null;

  let finalCells = null
  let clickedCellIndex = state.cells.findIndex((cell) => {
    return (action.position[0] > cell.minX && action.position[0] < cell.maxX && action.position[1] > cell.minY && action.position[1] < cell.maxY)
  })
  if (clickedCellIndex > -1) {
    let selectedCellBorder = getSelectedBorder(
      state.cells[clickedCellIndex],
      action.position[0],
      action.position[1],
      state.zoneWidth,
      state.zoneHeight,
      action.shiftClick,
      selectedPosition,
      action.scale
    );
    if (!selectedCellBorder) {
      finalCells = state.cells.map(x => {
        return {
          ...x,
          selected:action.shiftClick ? x.selected : false,
          selectedBorder: null,
        }
      });
      finalCells[clickedCellIndex] = {
        ...state.cells[clickedCellIndex],
        selected:true,
        selectedBorder: null
      }
    }
  }

  if (!finalCells) {
    // If we get this far it's because we clicked near a border
    finalCells = state.cells.map(c => {
      const selectedBorder = getSelectedBorder(
        c,
        action.position[0],
        action.position[1],
        state.zoneWidth,
        state.zoneHeight,
        action.shiftClick,
        selectedPosition,
        action.scale
      );
      if (selectedPosition === null && selectedBorder !== null) {
        switch (selectedBorder) {
          case "bottom":
            selectedPosition = c.maxY;
            break;
          case "top":
            selectedPosition = c.minY;
            break;
          case "left":
            selectedPosition = c.minX;
            break;
          case "right":
            selectedPosition = c.maxX;
            break;
        }
      }
      if (selectedBorder === c.selectedBorder) {
        return { 
          ...c,
          selected: false
        };
      }
      return {
        ...c,
        selectedBorder,
        selected: false
      };
    });
  }

  // ensure we only select a horizontal or vertical border
  if (finalCells.some(c => c.selectedBorder === "left" || c.selectedBorder === "right")) {
    finalCells = finalCells.reduce((prev, next, index) => {
      switch (next.selectedBorder) {
        case "bottom":
        case "top":
          // remove other cells with top or bottom borders
          prev[index] = { ...next, selectedBorder: null };
          return prev;
        default:
          return prev;
      }
    }, finalCells);
  }
  finalCells
    .filter(c => c.selectedBorder === "left")
    .forEach(c => {
      // find the cells to the left of this one that are not marked as "right" selected
      for (let i = 0; i < finalCells.length; i++) {
        if (finalCells[i].maxX === c.minX && finalCells[i].selectedBorder !== "right") {
          if (c.minY >= finalCells[i].maxY || c.maxY <= finalCells[i].minY) {
            continue;
          }
          finalCells[i] = { ...finalCells[i], selectedBorder: "right" };
        }
      }
    });
    finalCells
    .filter(c => c.selectedBorder === "right")
    .forEach(c => {
      // find the cells to the right of this one that are not marked as "left" selected
      for (let i = 0; i < finalCells.length; i++) {
        if (finalCells[i].minX === c.maxX && finalCells[i].selectedBorder !== "left") {
          if (c.minY >= finalCells[i].maxY || c.maxY <= finalCells[i].minY) {
            continue;
          }
          finalCells[i] = { ...finalCells[i], selectedBorder: "left" };
        }
      }
    });
    finalCells
    .filter(c => c.selectedBorder === "top")
    .forEach(c => {
      // find the cells to the left of this one that are not marked as "right" selected
      for (let i = 0; i < finalCells.length; i++) {
        if (finalCells[i].maxY === c.minY && finalCells[i].selectedBorder !== "bottom") {
          if (c.minX >= finalCells[i].maxX || c.maxX <= finalCells[i].minX) {
            continue;
          }
          finalCells[i] = { ...finalCells[i], selectedBorder: "bottom" };
        }
      }
    });
    finalCells
    .filter(c => c.selectedBorder === "bottom")
    .forEach(c => {
      // find the cells to the left of this one that are not marked as "right" selected
      for (let i = 0; i < finalCells.length; i++) {
        if (finalCells[i].minY === c.maxY && finalCells[i].selectedBorder !== "top") {
          if (c.minX >= finalCells[i].maxX || c.maxX <= finalCells[i].minX) {
            continue;
          }
          finalCells[i] = { ...finalCells[i], selectedBorder: "top" };
        }
      }
    });
  const minX = finalCells.reduce((prev, next) => {
    if (next.selectedBorder === "right") {
      return Math.max(next.minX, prev);
    }
    return prev;
  }, 0);
  const minY = finalCells.reduce((prev, next) => {
    if (next.selectedBorder === "bottom") {
      return Math.max(next.minY, prev);
    }
    return prev;
  }, 0);
  const maxX = finalCells.reduce((prev, next) => {
    if (next.selectedBorder === "left") {
      return Math.min(next.maxX, prev);
    }
    return prev;
  }, state.zoneWidth);
  const maxY = finalCells.reduce((prev, next) => {
    if (next.selectedBorder === "top") {
      return Math.min(next.maxY, prev);
    }
    return prev;
  }, state.zoneHeight);

  return {
    ...state,
    cells: finalCells,
    dragOffset: {
      maxX,
      maxY,
      minX,
      minY,
      x: 0,
      y: 0,
    },
    mouseDragStart: action.position,
    showTableUI: true,
  };
};
